import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  /* Landing back from LoginHub counts as loading even though there is no token
     yet — the ticket is about to be redeemed for one. Without this the first
     render has no token and no user, so the login form paints for a frame
     before the dashboard replaces it, which reads as a failed sign-in. */
  const [loading, setLoading] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (new URLSearchParams(window.location.search).get('sso') === '1') return true;
    return !!api.getToken();
  });

  useEffect(() => {
    /* ── Returning from AMS LoginHub ──────────────────────────────────────
       /auth/callback has already verified the id_token and matched the person
       to an HRMS record; it left a single-use, two-minute ticket in an
       HttpOnly cookie and sent the browser here with ?sso=1. Redeem it for an
       ordinary session, then take the marker out of the URL so a refresh does
       not try to redeem a ticket that is already spent.

       Deliberately ahead of the stored-token check: arriving with ?sso=1 means
       a fresh sign-in was just completed, and that should win over whatever
       token happens to be left in this tab. */
    const params = new URLSearchParams(window.location.search);
    if (params.get('sso') === '1') {
      setLoading(true);
      api.ssoExchange()
        .then((res) => {
          api.setToken(res.token, res.user?.role);
          setUser(res.user);
          window.history.replaceState({}, '', window.location.pathname);
        })
        .catch((e) => {
          // The ticket expired or was already used. Back to the form, with the
          // reason — password login is unaffected and still works.
          window.history.replaceState({}, '', '/login?sso_error=' +
            encodeURIComponent(e.message || 'sign_in_failed'));
        })
        .finally(() => setLoading(false));
      return;
    }

    const token = api.getToken();
    if (token) {
      api.getMe().then(u => { setUser(u); setLoading(false); }).catch(() => { api.clearToken(); setLoading(false); });
    }
  }, []);

  const login = useCallback(async (id, password, expectedRole) => {
    const res = await api.login(id, password, expectedRole);
    // 2FA enabled → no session yet; hand the challenge back to the login screen.
    if (res && res.twoFactorRequired) {
      return { twoFactorRequired: true, challengeToken: res.challengeToken, password };
    }
    api.setToken(res.token, res.user?.role);
    sessionStorage.setItem('_cp', password);
    setUser(res.user);
    return res.user;
  }, []);

  // Second login step (password path only): exchange the challenge token + 6-digit
  // code for a real session.
  const verifyTwoFactor = useCallback(async (challengeToken, code, password) => {
    const res = await api.twoFactorVerify(challengeToken, code);
    api.setToken(res.token, res.user?.role);
    if (password) sessionStorage.setItem('_cp', password);
    setUser(res.user);
    return res.user;
  }, []);

  // Google sign-in: exchange the Google authorization code (+ selected role) for a
  // real session. 2FA applies here too — if the user has it on, hand the challenge
  // back to the login screen (same as password login).
  const loginWithGoogle = useCallback(async (code, expectedRole) => {
    const res = await api.loginWithGoogle(code, expectedRole);
    if (res && res.twoFactorRequired) {
      return { twoFactorRequired: true, challengeToken: res.challengeToken };
    }
    api.setToken(res.token, res.user?.role);
    setUser(res.user);
    return res.user;
  }, []);

  /* Sign out lands on the login form, not the marketing page.
     Dashboards never touch the URL, so a signed-in person is sitting on "/".
     Dropping the user there re-renders PublicView, which reads the path it
     finds — "/" — and draws the LANDING page. Every other sign-out path in
     the app (api.logout, api.logoutAll, the 401 handler, SocketContext) sends
     you to /login; this one has to say so too. pushState rather than
     location.href keeps it one React render instead of a full reload. */
  const logout = useCallback(() => {
    api.clearToken();
    const at = typeof window === 'undefined' ? '' : window.location.pathname;
    if (at && at !== '/login' && at !== '/login/') {
      window.history.pushState({}, '', '/login');
    }
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const u = await api.getMe();
    setUser(u);
  }, []);

  // Multi-role: switch the active role → new token → reload into that dashboard
  const switchRole = useCallback(async (role) => {
    const res = await api.switchRole(role);
    api.setToken(res.token, res.user?.role);
    setUser(res.user);
    // Reload so the role-based top-level router renders the new dashboard cleanly
    window.location.reload();
    return res.user;
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, login, verifyTwoFactor, loginWithGoogle, logout, loading, refreshUser, switchRole }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
